import { useQueryClient } from "@tanstack/react-query";
import { Home, Ticket } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthProvider";
import { Button } from "../components/Button";
import { CheckboxField, FormAlert, TextField, focusFirstInvalid } from "../components/fields";
import { useDocumentTitle } from "../components/layout";
import { OfflineBanner, WriteGuard } from "../components/Offline";
import { errorMessage, fieldErrors, post } from "../lib/api";
import type { HouseholdDetail } from "../lib/types";
import { AuthLayout } from "./auth/AuthLayout";
import { safeNext, takeNext } from "./guards";

/** Extract an invite code from a pasted code or full /join/<code> link. */
export function parseInviteCode(input: string): string {
  const trimmed = input.trim();
  const match = /\/join\/([^/?#\s]+)/.exec(trimmed);
  return (match ? match[1] : trimmed).trim();
}

export function OnboardingPage() {
  const { me, selectHousehold, refreshMe, signOut, config } = useAuth();
  const [params] = useSearchParams();
  const adding = params.get("add") === "1";
  useDocumentTitle(adding ? "Add a household" : "Set up your kitchen");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createRef = useRef<HTMLFormElement>(null);
  const joinRef = useRef<HTMLFormElement>(null);
  const [name, setName] = useState("");
  const [samples, setSamples] = useState(true);
  const [nameError, setNameError] = useState<string | undefined>();
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | undefined>();

  if (me && me.households.length && !adding) return <Navigate to={safeNext(params.get("next"), takeNext())} replace />;

  const create = async (event: FormEvent) => {
    event.preventDefault();
    if (creating) return;
    if (!name.trim()) {
      setNameError("Give your household a name, like “The Rivera kitchen”.");
      focusFirstInvalid(createRef.current);
      return;
    }
    setNameError(undefined);
    setCreateError(null);
    setCreating(true);
    try {
      const household = await post<HouseholdDetail>("/api/v1/households", { name: name.trim(), ...(config.sampleRecipesAvailable ? { includeSampleRecipes: samples } : {}) });
      await refreshMe();
      await selectHousehold(household.id);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] === "h" });
      toast.success(`Created ${household.name}`);
      navigate(safeNext(params.get("next"), adding ? "/recipes" : takeNext()), { replace: true });
    } catch (error) {
      const fields = fieldErrors(error);
      if (fields.name) {
        setNameError(fields.name);
        focusFirstInvalid(createRef.current);
      } else setCreateError(errorMessage(error));
      setCreating(false);
    }
  };

  const join = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseInviteCode(code);
    if (!parsed) {
      setCodeError("Paste the invite code or link you were sent.");
      focusFirstInvalid(joinRef.current);
      return;
    }
    navigate(`/join/${encodeURIComponent(parsed)}`);
  };

  return (
    <>
      <OfflineBanner />
      <AuthLayout
        title={adding ? "Add a household" : "Set up your kitchen"}
        lead={adding ? "Create another household or join one you've been invited to." : "Create a household for the people you cook with, or join one with an invite."}
        footer={
          adding ? (
            <Button emphasis="ghost" intent="neutral" onClick={() => navigate("/recipes")}>
              Back to my kitchen
            </Button>
          ) : (
            <Button emphasis="ghost" intent="neutral" onClick={() => void signOut()}>
              Sign out
            </Button>
          )
        }
      >
        <WriteGuard>
          <form ref={createRef} noValidate onSubmit={create} className="onboard-card">
            <h2 className="onboard-card__title">
              <Home size={20} aria-hidden="true" /> Create a household
            </h2>
            <FormAlert>{createError}</FormAlert>
            <TextField label="Household name" value={name} error={nameError} maxLength={80} placeholder="The Rivera kitchen" onChange={(e) => setName(e.target.value)} required />
            {config.sampleRecipesAvailable ? (
              <CheckboxField label="Start with sample recipes" hint="A few private copies to explore cooking mode and groceries. Edit or delete them any time." checked={samples} onChange={(e) => setSamples(e.target.checked)} />
            ) : null}
            <Button type="submit" emphasis="solid" intent="brand" size="lg" block busy={creating}>
              Create household
            </Button>
          </form>
          <div className="onboard-or" aria-hidden="true">
            <span>or</span>
          </div>
          <form ref={joinRef} noValidate onSubmit={join} className="onboard-card">
            <h2 className="onboard-card__title">
              <Ticket size={20} aria-hidden="true" /> Join with an invite
            </h2>
            <TextField label="Invite code or link" value={code} error={codeError} autoCapitalize="none" spellCheck={false} autoComplete="off" onChange={(e) => setCode(e.target.value)} mono />
            <Button type="submit" emphasis="outline" intent="neutral" size="lg" block>
              Continue
            </Button>
          </form>
        </WriteGuard>
      </AuthLayout>
    </>
  );
}
